import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import type {
  Lead, LeadCreate, LeadUpdate, LeadQualify, LeadStats,
  Company, CompanyCreate, CNPJLookupResponse,
  Contact, ContactCreate, ContactUpdate,
  PaginatedResponse, LeadStatus, LeadOrigin,
} from '@/types'

// ==========================================
// Lead Hooks
// ==========================================

interface UseLeadsParams {
  page?: number
  perPage?: number
  search?: string
  status?: LeadStatus | null
  origin?: LeadOrigin | null
  ownerId?: string | null
}

export function useLeads(params: UseLeadsParams = {}) {
  return useQuery({
    queryKey: ['leads', params],
    queryFn: async () => {
      const query: Record<string, string | number> = {
        page: params.page || 1,
        per_page: params.perPage || 20,
      }
      if (params.search) query.search = params.search
      if (params.status) query.status = params.status
      if (params.origin) query.origin = params.origin
      if (params.ownerId) query.owner_id = params.ownerId
      const { data } = await api.get<PaginatedResponse<Lead>>('/v1/leads/', { params: query })
      return data
    },
  })
}

export function useLead(leadId: string | null) {
  return useQuery({
    queryKey: ['leads', leadId],
    queryFn: async () => {
      const { data } = await api.get<Lead>(`/v1/leads/${leadId}`)
      return data
    },
    enabled: !!leadId,
  })
}

export interface LeadScoreBreakdown {
  total: number
  company_completeness: { score: number; max: number; details: any }
  engagement: { score: number; max: number; details: any }
  bant: { score: number; max: number; details: any }
  profile_fit: { score: number; max: number; details: any }
}

export function useLeadScoreBreakdown(leadId: string | null) {
  return useQuery({
    queryKey: ['lead-score-breakdown', leadId],
    queryFn: async () => {
      const { data } = await api.get<LeadScoreBreakdown>(`/v1/leads/${leadId}/score-breakdown`)
      return data
    },
    enabled: !!leadId,
  })
}

export interface TimelineEvent {
  type: string
  timestamp: string
  updated_at?: string | null
  title: string
  content?: string | null
  user_name?: string | null
  extra?: Record<string, any>
}

export function useLeadTimeline(leadId: string | null) {
  return useQuery({
    queryKey: ['lead-timeline', leadId],
    queryFn: async () => {
      const { data } = await api.get<{ items: TimelineEvent[]; total: number }>(`/v1/leads/${leadId}/timeline`)
      return data
    },
    enabled: !!leadId,
  })
}

export function useLeadStats() {
  return useQuery({
    queryKey: ['leads', 'stats'],
    queryFn: async () => {
      const { data } = await api.get<LeadStats>('/v1/leads/stats/overview')
      return data
    },
  })
}

export interface SubOriginSuggestion { value: string; count: number }

export function useSubOrigins(origin?: string) {
  return useQuery({
    queryKey: ['leads', 'sub-origins', origin || 'all'],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (origin) params.origin = origin
      const { data } = await api.get<SubOriginSuggestion[]>('/v1/leads/sub-origins/list', { params })
      return data
    },
    staleTime: 30_000,
  })
}

export function useCreateLead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (leadData: LeadCreate) => {
      const { data } = await api.post<Lead>('/v1/leads/', leadData)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  })
}

export function useUpdateLead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ leadId, data: leadData }: { leadId: string; data: LeadUpdate }) => {
      const { data } = await api.put<Lead>(`/v1/leads/${leadId}`, leadData)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  })
}

export function useQualifyLead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ leadId, data: qualifyData }: { leadId: string; data: LeadQualify }) => {
      const { data } = await api.post<Lead>(`/v1/leads/${leadId}/qualify`, qualifyData)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  })
}

export function useDeleteLead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (leadId: string) => {
      await api.delete(`/v1/leads/${leadId}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leads'] }),
  })
}


export function useLeadSegments() {
  return useQuery({
    queryKey: ['leads', 'segments'],
    queryFn: async () => {
      const { data } = await api.get<{ segments: Record<string, number> }>('/v1/leads/stats/segments')
      return data.segments
    },
  })
}

export function useLeadOrigins() {
  return useQuery({
    queryKey: ['leads', 'origins'],
    queryFn: async () => {
      const { data } = await api.get<{ origins: Record<string, number> }>('/v1/leads/stats/origins')
      return data.origins
    },
  })
}

// ==========================================
// Company Hooks
// ==========================================

export function useCompanies(search?: string) {
  return useQuery({
    queryKey: ['companies', search],
    queryFn: async () => {
      const query: Record<string, string | number> = { per_page: 100 }
      if (search) query.search = search
      const { data } = await api.get<{ items: Company[] }>('/v1/companies/', { params: query })
      return data.items
    },
  })
}

export function useCompany(companyId: string | null) {
  return useQuery({
    queryKey: ['companies', companyId],
    queryFn: async () => {
      const { data } = await api.get<Company>(`/v1/companies/${companyId}`)
      return data
    },
    enabled: !!companyId,
  })
}

export function useLookupCNPJ() {
  return useMutation({
    mutationFn: async (cnpj: string) => {
      const { data } = await api.post<CNPJLookupResponse>('/v1/companies/lookup-cnpj', { cnpj })
      return data
    },
  })
}

export function useCreateCompanyFromCNPJ() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (cnpj: string) => {
      const { data } = await api.post<Company>(
        '/v1/companies/from-cnpj',
        { cnpj },
        { params: { create_contacts: true } }
      )
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['companies'] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

export function useCreateCompany() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (companyData: CompanyCreate) => {
      const { data } = await api.post<Company>('/v1/companies/', companyData)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['companies'] }),
  })
}

export function useUpdateCompany() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ companyId, data }: { companyId: string; data: Partial<Company> }) => {
      const { data: out } = await api.put<Company>(`/v1/companies/${companyId}`, data)
      return out
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['companies'] })
      qc.invalidateQueries({ queryKey: ['company', vars.companyId] })
      qc.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}

export interface SnovEnrichResult {
  domain: string
  domain_emails_in_snov: number
  contacts_enriched: number
  // novo schema
  auto_filled?: SnovEnrichDetail[]
  suggestions_to_review?: SnovEnrichDetail[]
  // legado
  details?: { contact_id: string; name: string; email: string; status?: string }[]
  credits_balance: number | null
}

export interface SnovSuggestion {
  first_name: string
  last_name: string
  role: string
  email: string | null
  snov_status?: string | null
  email_source: 'snov' | null
  already_exists: boolean
}

export interface SnovSuggestResult {
  domain: string
  candidates_found: number
  suggestions: SnovSuggestion[]
  credits_balance: number | null
  message?: string
}

/**
 * Combina Claude (descoberta de nomes do ICP via web search) com Snov (Email Finder)
 * pra sugerir contatos que provavelmente são decisores na empresa.
 */
export function useSnovSuggestContacts() {
  return useMutation({
    mutationFn: async (companyId: string) => {
      // Endpoint pesado (Claude web search + Snov Email Finder com variações).
      // Timeout grande pra não cair no axios default de 60s.
      const { data } = await api.post<SnovSuggestResult>(
        `/v1/companies/${companyId}/snovio-suggest-contacts`,
        undefined,
        { timeout: 4 * 60_000 },
      )
      return data
    },
  })
}

export interface BulkContactItem {
  first_name: string
  last_name?: string | null
  email?: string | null
  job_title?: string | null
}

export function useBulkCreateContacts() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ companyId, contacts }: { companyId: string; contacts: BulkContactItem[] }) => {
      const { data } = await api.post<{ created: number; skipped_duplicates: number }>(
        `/v1/companies/${companyId}/contacts/bulk-create`,
        { contacts },
      )
      return data
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['contacts', 'company', vars.companyId] })
      qc.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}

export interface SnovEnrichDetail {
  contact_id: string
  name: string
  current_email?: string | null
  suggested_email: string
  snov_status?: string | null
  variation_used?: string | null
  applied: boolean
}

export interface SnovEnrichResponse {
  domain: string
  domain_emails_in_snov: number
  contacts_enriched: number
  auto_filled: SnovEnrichDetail[]
  suggestions_to_review: SnovEnrichDetail[]
  credits_balance: number | null
  // legado
  details?: any[]
}

/**
 * Aplica um email sugerido pela Snov a um contato existente.
 */
export function useApplySnovSuggestion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ companyId, contactId, email }: { companyId: string; contactId: string; email: string }) => {
      const { data } = await api.post(`/v1/companies/${companyId}/contacts/apply-email`, { contact_id: contactId, email })
      return data
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['contacts', 'company', vars.companyId] })
      qc.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}

/**
 * Enriquece contatos da empresa via Snov.io.
 * - Contato sem email → preenche automaticamente
 * - Contato com freemail (@gmail, @hotmail, etc) → sugere corporativo SEM sobrescrever
 * - Contato com email corporativo → pula
 */
export function useSnovEnrichCompany() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (companyId: string) => {
      // Timeout grande — Snov Email Finder com variações + N contatos pode passar de 60s
      const { data } = await api.post<SnovEnrichResult>(
        `/v1/companies/${companyId}/snovio-enrich`,
        undefined,
        { timeout: 4 * 60_000 },
      )
      return data
    },
    onSuccess: (_data, companyId) => {
      qc.invalidateQueries({ queryKey: ['contacts', 'company', companyId] })
      qc.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}

/**
 * Re-sincroniza dados cadastrais da empresa com a Receita Federal (BrasilAPI).
 * Usado após edição/inclusão de CNPJ para repopular automaticamente razão social,
 * nome fantasia, CNAE, endereço, telefone, e-mail e situação cadastral.
 */
export function useSyncCompanyCnpj() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ companyId, syncContacts = false }: { companyId: string; syncContacts?: boolean }) => {
      const { data: out } = await api.post<Company>(
        `/v1/companies/${companyId}/sync-cnpj`,
        null,
        { params: { sync_contacts: syncContacts } },
      )
      return out
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['companies'] })
      qc.invalidateQueries({ queryKey: ['company', vars.companyId] })
      qc.invalidateQueries({ queryKey: ['leads'] })
      qc.invalidateQueries({ queryKey: ['contacts', 'company', vars.companyId] })
    },
  })
}

// ==========================================
// Saved Filters (filtros nomeados persistidos por usuário)
// ==========================================

export interface SavedFilter {
  id: string
  scope: string
  name: string
  filters: Record<string, any>
}

export function useSavedFilters(scope: string = 'leads') {
  return useQuery({
    queryKey: ['saved-filters', scope],
    queryFn: async () => {
      const { data } = await api.get<SavedFilter[]>('/v1/saved-filters', { params: { scope } })
      return data
    },
    staleTime: 30000,
  })
}

export function useUpsertSavedFilter() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: { scope: string; name: string; filters: Record<string, any> }) => {
      const { data } = await api.post<SavedFilter>('/v1/saved-filters', body)
      return data
    },
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: ['saved-filters', vars.scope] }),
  })
}

export function useDeleteSavedFilter() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/v1/saved-filters/${id}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['saved-filters'] }),
  })
}

// ==========================================
// Contact Hooks
// ==========================================

export function useCompanyContacts(companyId: string | null) {
  return useQuery({
    queryKey: ['contacts', 'company', companyId],
    queryFn: async () => {
      const { data } = await api.get<{ items: Contact[] }>(`/v1/contacts/company/${companyId}`)
      return data.items
    },
    enabled: !!companyId,
  })
}

// Keep alias for backward compatibility
export function useContacts(companyId: string | null) {
  return useCompanyContacts(companyId)
}

export function useCreateContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (contactData: ContactCreate) => {
      const { data } = await api.post<Contact>('/v1/contacts/', contactData)
      return data
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['contacts', 'company', vars.company_id] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

export function useUpdateContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ contactId, data: contactData }: { contactId: string; data: ContactUpdate }) => {
      const { data } = await api.put<Contact>(`/v1/contacts/${contactId}`, contactData)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

export function useDeleteContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (contactId: string) => {
      await api.delete(`/v1/contacts/${contactId}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

export function useSetPrimaryContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (contactId: string) => {
      const { data } = await api.post<Contact>(`/v1/contacts/${contactId}/set-primary`)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

// ─── Bulk operations ────────────────────────────────────────────────────────
export interface BulkLeadsResult { affected: number; blocked?: number; blocked_ids?: string[] }

export function useBulkUpdateLeads() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { lead_ids: string[]; owner_id?: string; status?: string; origin?: string }) => {
      const { data } = await api.patch<BulkLeadsResult>('/v1/leads/bulk', payload)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}

export function useBulkDeleteLeads() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (lead_ids: string[]) => {
      const { data } = await api.post<BulkLeadsResult>('/v1/leads/bulk-delete', { lead_ids })
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}
